import React, { useState, useEffect, useCallback } from 'react';
import { FolderTree, Loader2, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

interface BuyBoxIndicatorProps {
  isBuyboxWinner: boolean;
  buyboxId: string | null;
  totalVariants?: number;
  seller?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
  showExpandButton?: boolean;
  tableName?: string;
  onVariantsLoaded?: (variants: any[]) => void;
}

const BuyBoxIndicator: React.FC<BuyBoxIndicatorProps> = ({
  isBuyboxWinner,
  buyboxId,
  totalVariants = 0,
  seller,
  isExpanded = false,
  onToggle,
  showExpandButton = false,
  tableName,
  onVariantsLoaded
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [variantsCount, setVariantsCount] = useState(0);
  const [hasLoadedVariants, setHasLoadedVariants] = useState(false);
  
  // Log cuando el componente se renderiza

  const loadVariants = useCallback(async () => {
    
    if (!buyboxId || !onVariantsLoaded) {
      return;
    }
    
    setLoading(true);
    
    try {
      let url;
      let response;
      
      if (tableName && tableName !== 'comparison') {
        // Caso normal: tenemos un tableName válido
        url = `/api/tables/${tableName}/buybox/${buyboxId}`;
      } else {
        // Caso comparación: usar endpoint alternativo
        url = `/api/buybox/${buyboxId}/variants`;
      }
      
      response = await api.get(url);
      const data = response.data;
      
      // Update count if not already set
      if (variantsCount <= 0) {
        setVariantsCount(data.totalVariants || 1);
      }
      
      // Always pass variants to parent
      const variantsToPass = data.variants || [];
      
      try {
        onVariantsLoaded(variantsToPass);
      } catch (callbackError) {
        console.error('❌ Error calling onVariantsLoaded:', callbackError);
      }
      
      setHasLoadedVariants(true);
    } catch (error: any) {
      console.error('❌ Error loading variants:', error);
      // Still call with empty array on error
      onVariantsLoaded([]);
    } finally {
      setLoading(false);
    }
  }, [tableName, buyboxId, variantsCount]); // Remove onVariantsLoaded to prevent re-renders

  // Load count once when component mounts or when dependencies change
  useEffect(() => {
    
    if (tableName && buyboxId && variantsCount === 0 && tableName !== 'comparison') {
      // Simple approach: just show "..." while loading
      setVariantsCount(-1); // -1 means loading
      
      const url = `/api/tables/${tableName}/buybox/${buyboxId}`;
      
      api.get(url)
        .then(response => {
          setVariantsCount(response.data.totalVariants || 1);
        })
        .catch((error) => {
          console.error('❌ Error fetching variants count:', error);
          setVariantsCount(1); // Default to 1 on error
        });
    } else if (tableName === 'comparison' && buyboxId && variantsCount === 0) {
      // Para modo comparación, usar el endpoint alternativo
      setVariantsCount(-1); // -1 means loading
      
      const url = `/api/buybox/${buyboxId}/variants`;
      
      api.get(url)
        .then(response => {
          setVariantsCount(response.data.totalVariants || response.data.variants?.length || 1);
        })
        .catch((error) => {
          console.error('❌ Error fetching variants count in comparison mode:', error);
          setVariantsCount(1); // Default to 1 on error
        });
    }
  }, [tableName, buyboxId]); // Re-run when tableName or buyboxId changes

  // Cargar variantes cuando se expande
  useEffect(() => {
    
    if (isExpanded && tableName && buyboxId && onVariantsLoaded && !loading && !hasLoadedVariants) {
      // Only load variants once when expanded
      loadVariants();
    }
  }, [isExpanded, tableName, buyboxId]); // Remove loadVariants and loading to prevent loops

  // Si no hay buybox_id, no mostrar nada
  if (!buyboxId) {
    return null;
  }

  return (
    <div className="flex items-center space-x-1">
      <button
        onClick={() => {
          if (onToggle) onToggle();
        }}
        disabled={loading || !tableName}
        className={`
          flex items-center space-x-1 px-2 py-1 rounded-md transition-all
          ${tableName 
            ? isBuyboxWinner 
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'
            : 'bg-gray-100 text-gray-500 cursor-not-allowed'
          }
        `}
        title={tableName 
          ? (isExpanded ? t('buybox.collapse') : t('buybox.expand'))
          : 'Configure tableName to enable expansion'
        }
      >
        {isBuyboxWinner ? (
          <Trophy className="h-4 w-4" />
        ) : (
          <FolderTree className="h-4 w-4" />
        )}
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <span className="text-xs font-medium">
              {variantsCount === -1 ? '...' : variantsCount > 0 ? variantsCount : '?'}
            </span>
          </>
        )}
      </button>
    </div>
  );
};

export default BuyBoxIndicator;